import React from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../../../app/hooks";
import { RootState } from "@/app/rootReducer";
import SearchResultCard from "../SearchResultCard/SearchResultCard";
import { CalendarIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { performSearch } from "../../slices/searchSlice";
import InfiniteScroll from "react-infinite-scroll-component";

const SearchResults: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    searchResults,
    isSearching,
    searchQuery,
    selectedFilters,
    currentPage,
    hasMore,
  } = useSelector((state: RootState) => state.search);

  // NOTE: Events temporarily disabled
  const hasResults = searchResults.resources.length > 0;

  // Load more function for infinite scroll
  const loadMore = () => {
    if (!isSearching && hasMore) {
      dispatch(
        performSearch({
          query: searchQuery,
          filters: selectedFilters,
          page: currentPage + 1,
        }),
      );
    }
  };

  if (isSearching && currentPage === 0) {
    return (
      <div className="p-4 sm:p-6 text-center">
        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400 text-sm sm:text-base">Searching...</p>
      </div>
    );
  }

  if (!hasResults) {
    if (searchQuery.trim()) {
      return (
        <div className="p-4 sm:p-6 text-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">
            No results found for "
            <span className="font-medium text-gray-700 dark:text-gray-300">{searchQuery}</span>"
          </p>
          <p className="mt-2 text-xs sm:text-sm text-gray-400 dark:text-gray-500">
            Try adjusting your search terms or filters
          </p>
        </div>
      );
    } else {
      return (
        <div className="p-4 sm:p-6 text-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">
            No content available
          </p>
          <p className="mt-2 text-xs sm:text-sm text-gray-400 dark:text-gray-500">
            Try searching for specific topics or adjusting your filters
          </p>
        </div>
      );
    }
  }

  // Calculate total items for infinite scroll (events disabled)
  const totalItems = searchResults.resources.length;

  return (
    <div id="scrollableDiv" style={{ height: "100%", overflow: "auto" }}>
      <InfiniteScroll
        dataLength={totalItems}
        hasMore={hasMore}
        loader={
          <div className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              <span className="text-gray-500 dark:text-gray-400">Loading more results...</span>
            </div>
          </div>
        }
        next={loadMore}
        scrollableTarget="scrollableDiv"
      >
        <div className="divide-y divide-gray-200 dark:divide-slate-800">
          {/* NOTE: Events Section temporarily disabled */}
          {/* {searchResults.events.length > 0 && (
            <div className="p-3 sm:p-4">
              <div className="flex items-center mb-2 sm:mb-3">
                <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 dark:text-gray-500 mr-2" />
                <h3 className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                  Events ({searchResults.totalEvents})
                </h3>
              </div>
              <div className="space-y-2">
                {searchResults.events.map((event) => (
                  <SearchResultCard
                    key={event.objectID}
                    result={event}
                    type="event"
                  />
                ))}
              </div>
            </div>
          )} */}

          {/* Resources Section */}
          {searchResults.resources.length > 0 && (
            <div className="p-3 sm:p-4">
              <div className="flex items-center mb-2 sm:mb-3">
                <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 dark:text-gray-500 mr-2" />
                <h3 className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                  Resources ({searchResults.totalResources})
                </h3>
              </div>
              <div className="space-y-2">
                {searchResults.resources.map((resource) => (
                  <SearchResultCard
                    key={resource.objectID}
                    result={resource}
                    type="resource"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </InfiniteScroll>
    </div>
  );
};

export default SearchResults;
